import * as React from 'react';
import { Box, Divider, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/core/v2/toaster';

import { updateResidentCustomFieldValues } from '@/api/residents';
import { SectionLabel } from '@/pages/v2/requests/request/components/request-details-card';

export interface CustomFieldDef {
  id: string;
  name: string;
  key: string;
  visible_to_member: boolean;
  editable_by_member: boolean;
  sort_order: number;
}

interface ResidentCustomFieldsProps {
  communityId: string;
  residentId: string;
  customFields: CustomFieldDef[];
  customFieldValues: Record<string, string>;
  isManager: boolean;
  isViewingSelf: boolean;
  queryKey: (string | undefined)[];
}

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1.25, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: '0.8125rem', flexShrink: 0, minWidth: 90 }}
        variant="body2"
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

export function ResidentCustomFields({
  communityId,
  residentId,
  customFields,
  customFieldValues,
  isManager,
  isViewingSelf,
  queryKey,
}: ResidentCustomFieldsProps): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const [values, setValues] = React.useState<Record<string, string>>(() => ({ ...customFieldValues }));

  React.useEffect(() => {
    setValues((prev) => ({ ...customFieldValues, ...prev }));
  }, [customFieldValues]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, string | null>) =>
      updateResidentCustomFieldValues(communityId, residentId, payload),
    onSuccess: (res, variables) => {
      setValues((prev) => ({ ...prev, ...res.data }));
      queryClient.invalidateQueries({ queryKey });
      const names = customFields.filter((f) => f.id in (variables ?? {})).map((f) => f.name);
      const suffix = names.length > 0 ? `: ${names.join(', ')}` : '';
      toast.success(`Saved${suffix}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to save');
    },
  });

  const handleSave = () => {
    mutation.mutate(values);
  };

  if (!customFields || customFields.length === 0) {
    return null;
  }

  const hasEditable = customFields.some(
    (f) => isManager || (isViewingSelf && f.editable_by_member)
  );

  return (
    <Box>
      <SectionLabel
        action={
          hasEditable ? (
            <LoadingButton
              size="small"
              variant="contained"
              loading={mutation.isPending}
              onClick={handleSave}
            >
              Save
            </LoadingButton>
          ) : undefined
        }
      >
        Additional details
      </SectionLabel>
      <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }} variant="caption">
        Custom fields for this resident (e.g. emergency contact, parking spot).
      </Typography>
      <Stack sx={{ mt: 1 }} divider={<Divider flexItem />}>
        {customFields.map((field) => {
          const editable = isManager || (isViewingSelf && field.editable_by_member);
          const value = values[field.id] ?? '';

          return (
            <KVRow key={field.id} label={field.name}>
              {editable ? (
                <TextField
                  size="small"
                  fullWidth
                  placeholder={`Enter ${field.name}`}
                  value={value}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                  }
                  sx={{ maxWidth: 360 }}
                />
              ) : (
                <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'} sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                  {value || '—'}
                </Typography>
              )}
            </KVRow>
          );
        })}
      </Stack>
    </Box>
  );
}
